import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Key } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

export default function ChangePasswordDialog({ open, onOpenChange }) {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);

    const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 6) return toast.error("New password must be at least 6 characters");
        if (next !== confirm) return toast.error("New password and confirmation do not match");
        setBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: current, new_password: next });
            toast.success("Password changed. Please sign in again next time.");
            reset();
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
            <DialogContent data-testid="change-password-dialog" className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Key size={20} weight="fill" className="text-yellow-600" />
                        Change your password
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500">
                        Enter your current password, then a new one you can remember.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Current password</Label>
                        <Input data-testid="cp-current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1.5" autoComplete="current-password" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">New password</Label>
                        <Input data-testid="cp-new" type="password" required value={next} onChange={(e) => setNext(e.target.value)} className="mt-1.5" autoComplete="new-password" />
                        <div className="mt-1 text-[11px] text-slate-500">Minimum 6 characters.</div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Confirm new password</Label>
                        <Input data-testid="cp-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" autoComplete="new-password" />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
                        <Button type="submit" data-testid="cp-submit" disabled={busy} className="bg-blue-600 text-white hover:bg-blue-700">
                            {busy ? "Saving…" : "Update password"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
